import React, { useRef } from 'react';
import { Button } from './ui/button';
import { MapPin, Building2, GraduationCap, ChevronLeft, ChevronRight } from 'lucide-react';
import { mockData } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import { useAuthGate } from '../context/AuthGateContext';

const PopularCities = ({ onCitySelect }) => {
  const { user } = useAuth();
  const { requireAuth } = useAuthGate();
  const scrollRef = useRef(null);

  const handleExploreCity = (cityName) => {
    if (!user) { requireAuth('/'); return; }
    onCitySelect(cityName);
    setTimeout(() => {
      document.getElementById('properties')?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  const handleViewAllCities = () => {
    if (!user) { requireAuth('/'); return; }
    onCitySelect(null);
    setTimeout(() => {
      document.getElementById('properties')?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  const scrollBy = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 340, behavior: 'smooth' });
  };

  return (
    <section id="cities" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#094f6f] mb-2">
              Popular Australian Cities
            </h2>
            <p className="text-gray-600 text-lg">
              Discover student accommodation across Australia's top study destinations
            </p>
          </div>

          {/* Arrow controls */}
          <div className="hidden md:flex items-center gap-2 flex-shrink-0 ml-8">
            <button
              onClick={() => scrollBy(-1)}
              className="w-10 h-10 rounded-full border-2 border-[#094f6f] text-[#094f6f] flex items-center justify-center hover:bg-[#094f6f] hover:text-white transition-all"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scrollBy(1)}
              className="w-10 h-10 rounded-full border-2 border-[#094f6f] text-[#094f6f] flex items-center justify-center hover:bg-[#094f6f] hover:text-white transition-all"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Horizontal scroll track */}
        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide"
          style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
        >
          {mockData.popularCities.map((city) => (
            <div
              key={city.id}
              onClick={() => handleExploreCity(city.name)}
              data-testid={`city-card-${city.name.toLowerCase()}`}
              className="flex-none w-64 cursor-pointer group"
              style={{ scrollSnapAlign: 'start' }}
            >
              {/* Tall image card */}
              <div className="relative h-80 rounded-2xl overflow-hidden shadow-md group-hover:shadow-xl transition-shadow duration-300">
                <img
                  src={city.image}
                  alt={city.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                {/* Property count top-right */}
                <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full px-2.5 py-1 text-xs font-semibold text-gray-800">
                  {city.properties} properties
                </div>

                {/* City info bottom */}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-xl font-bold text-white mb-0.5">{city.name}</h3>
                  <p className="text-white/70 text-xs flex items-center mb-3">
                    <MapPin className="w-3 h-3 mr-1" />{city.country}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-white font-bold text-sm">
                      from {city.startingPrice}
                      <span className="text-white/70 text-xs font-normal">/wk</span>
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleExploreCity(city.name); }}
                      className="bg-[#094f6f] text-white text-xs px-3 py-1.5 rounded-full font-semibold hover:bg-[#094f6f]/90 transition-colors"
                      data-testid={`explore-${city.name.toLowerCase()}-btn`}
                    >
                      Explore
                    </button>
                  </div>
                </div>
              </div>

              {/* Universities below card */}
              <div className="mt-3 px-1">
                <div className="flex items-start gap-1.5 text-gray-500 text-xs">
                  <GraduationCap className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#094f6f]" />
                  <span className="line-clamp-2">{city.universities.join(' · ')}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All + scroll hint */}
        <div className="flex items-center justify-between mt-8">
          <p className="text-sm text-gray-400 md:hidden">← Swipe to explore →</p>
          <Button
            variant="outline"
            className="ml-auto text-[#094f6f] border-[#094f6f] hover:bg-sky-50 px-6 rounded-full"
            onClick={handleViewAllCities}
            data-testid="view-all-cities-btn"
          >
            View All Cities
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Hide scrollbar globally for this section */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </section>
  );
};

export default PopularCities;
